import React, { useState } from 'react';
import WorkflowProgressTracker from '../../components/ui/WorkflowProgressTracker';
import ClaimTypeSelector from './components/ClaimTypeSelector';
import CreditSelector from './components/CreditSelector';
import EvidenceCompiler from './components/EvidenceCompiler';
import ClaimReview from './components/ClaimReview';
import ClaimStatusPanel from './components/ClaimStatusPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ClaimsBuilder = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [claimData, setClaimData] = useState({
    claimType: '',
    selectedCredits: [],
    evidenceFiles: [],
    companyInfo: {},
    verificationOptions: {}
  });

  const workflowSteps = [
    { id: 1, label: 'Claim Type', description: 'Select claim category' },
    { id: 2, label: 'Credits', description: 'Choose carbon credits' },
    { id: 3, label: 'Evidence', description: 'Upload documentation' },
    { id: 4, label: 'Review', description: 'Validate and submit' }
  ];

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const handleClaimTypeSelect = (type) => {
    setClaimData(prev => ({
      ...prev,
      claimType: type
    }));
  };

  const handleCreditsChange = (credits) => {
    setClaimData(prev => ({
      ...prev,
      selectedCredits: credits
    }));
  };

  const handleNext = () => {
    if (currentStep < workflowSteps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = () => {
    // Mock save draft functionality
    alert('Draft saved successfully!');
  };

  const handleSubmitClaim = () => {
    // Mock submit functionality
    alert('Claim submitted successfully! You will receive a confirmation email shortly.');
    // Reset form or redirect
    setCurrentStep(0);
    setClaimData({
      claimType: '',
      selectedCredits: [],
      evidenceFiles: [],
      companyInfo: {},
      verificationOptions: {}
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <ClaimTypeSelector
            selectedType={claimData?.claimType}
            onTypeSelect={handleClaimTypeSelect}
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <CreditSelector
            selectedCredits={claimData?.selectedCredits}
            onCreditsChange={handleCreditsChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 2:
        return (
          <EvidenceCompiler
            claimType={claimData?.claimType}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <ClaimReview
            claimData={claimData}
            onSubmit={handleSubmitClaim}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-primary rounded-lg">
                <Icon name="FileText" size={20} className="text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-card-foreground">Claims Builder</h1>
                <p className="text-sm text-muted-foreground">VCMI-compliant carbon credit retirement claims</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center space-x-2 px-3 py-1 bg-success/10 rounded-full">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-success">VCMI Claims Code v2024</span>
              </div>
              <Button variant="outline" size="sm" iconName="HelpCircle" iconPosition="left">
                Help
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Progress Tracker */}
            <WorkflowProgressTracker
              steps={workflowSteps}
              currentStep={currentStep}
              onStepClick={handleStepClick}
            />

            {/* Current Step Content */}
            <div className="min-h-96">
              {renderCurrentStep()}
            </div>

            {/* Help Section */}
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <div className="text-sm">
                  <h4 className="font-medium text-card-foreground mb-1">VCMI Claims Code Compliance</h4>
                  <p className="text-muted-foreground">
                    This builder ensures your claims meet VCMI Claims Code requirements. All submissions are validated 
                    against current standards and may be subject to third-party verification. For detailed guidance, 
                    refer to the official VCMI documentation.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status Panel */}
          <div className="lg:col-span-1">
            <ClaimStatusPanel
              selectedCredits={claimData?.selectedCredits}
              claimType={claimData?.claimType}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimsBuilder;